import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
/**
 * NodeOutputPreview Component
 *
 * Displays a smart preview of node execution output.
 * Extracts and formats key fields from the result object with intelligent rendering.
 */

import { cn, formatAddress, getExplorerTxUrl } from "@/lib/utils";
import { formatTokenAmount } from "@/components/ui/workflow-step-row";
import { isSolanaAddress } from "@/lib/utils/address";
import { isChainIdKey } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
import { camelToWords } from "@/utils/string";
import type { NodeExecutionStatus } from "@/types/runStream";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
interface NodeOutputPreviewProps {
  result: Record<string, unknown> | undefined;
  status: NodeExecutionStatus;
  maxFields?: number;
  className?: string;
  /** Result schema from action definition — used to detect format: "token-amount" fields */
  resultSchema?: Record<string, unknown>;
  /** Token decimals for formatting token-amount result fields (underlying asset) */
  tokenDecimals?: number;
  /** Vault share decimals for fields annotated with x-token-decimals: "vault" */
  vaultShareDecimals?: number;
}

// Priority fields to extract and display first
const PRIORITY_FIELDS = ["formatted", "value", "message", "output", "data", "result", "status", "success", "txHash", "hash"];

// Fields that should be rendered as markdown/text content
const TEXT_CONTENT_FIELDS = ["formatted", "message", "output", "text", "content", "body"];

// Fields that represent boolean success/failure status
const STATUS_FIELDS = ["success", "status", "ok"];

/**
 * Check if a string looks like an Ethereum address
 */
function isEthereumAddress(value: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(value);
}

/**
 * Check if a string looks like a transaction hash (EVM or Solana)
 */
function isTransactionHash(value: string): boolean {
  // EVM: 0x-prefixed 64 hex chars
  if (/^0x[a-fA-F0-9]{64}$/.test(value)) return true;
  // Solana: base58 signature, typically 87-88 chars
  if (/^[1-9A-HJ-NP-Za-km-z]{85,90}$/.test(value)) return true;
  return false;
}
const TX_HASH_KEYS = ["signature", "txhash", "transactionhash", "tx_hash", "hash"];

/**
 * Format a transaction hash for display (first8...last8)
 */
function formatTxHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}...${hash.slice(-8)}`;
}

/**
 * Check if a value represents a success status
 */
function isSuccessValue(key: string, value: unknown): boolean | null {
  const keyLower = key.toLowerCase();
  if (STATUS_FIELDS.some(f => keyLower.includes(f))) {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const valueLower = value.toLowerCase();
      if (valueLower === "success" || valueLower === "ok" || valueLower === "true") return true;
      if (valueLower === "failure" || valueLower === "failed" || valueLower === "error" || valueLower === "false") return false;
    }
  }
  return null;
}

/**
 * Check if a field should be rendered as text content
 */
function isTextContentField(key: string): boolean {
  return TEXT_CONTENT_FIELDS.some(f => key.toLowerCase().includes(f));
}

/**
 * Format a value for display (for non-special fields)
 */
function formatValue(value: unknown, maxLength = 80, key?: string): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";

  // Chain IDs are identifiers, not magnitudes — don't apply thousand separators.
  // e.g. `Chain Id: 8453` (Base), not `Chain Id: 8,453`.
  if (typeof value === "number" && key && isChainIdKey(key)) {
    return String(value);
  }
  if (typeof value === "string") {
    // Handle Ethereum addresses - format without quotes
    if (isEthereumAddress(value)) {
      return formatAddress(value);
    }
    // Handle Solana addresses - truncate without quotes
    if (isSolanaAddress(value)) {
      return formatAddress(value, 4, 4);
    }
    // Handle transaction hashes - format without quotes
    if (isTransactionHash(value)) {
      return formatTxHash(value);
    }
    // Other strings - truncate and wrap in quotes
    if (value.length > maxLength) {
      return `"${value.slice(0, maxLength)}..."`;
    }
    return `"${value}"`;
  }
  if (typeof value === "number") {
    if (Math.abs(value) >= 1000) {
      return value.toLocaleString();
    }
    if (!Number.isInteger(value)) {
      return value.toFixed(4).replace(/\.?0+$/, "");
    }
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    return `Array(${value.length})`;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length === 0) return "{}";
    return `{ ${keys.length} ${keys.length === 1 ? "key" : "keys"} }`;
  }
  return String(value);
}

/**
 * Format a key for display (convert camelCase to Title Case)
 */
function formatKey(key: string): string {
  return camelToWords(key).replace(/^./, str => str.toUpperCase());
}
function getSchemaFieldAttr(key: string, attr: string, resultSchema?: Record<string, unknown>): unknown {
  if (!resultSchema) return undefined;
  const properties = resultSchema.properties as Record<string, Record<string, unknown>> | undefined;
  return properties?.[key]?.[attr];
}

/**
 * Extract priority fields from result, sorted by priority
 */
function extractFields(result: Record<string, unknown>, maxFields: number): Array<{
  key: string;
  value: unknown;
  priority: number;
}> {
  const entries = Object.entries(result);
  const sortedEntries = entries.map(([key, value]) => {
    const priorityIndex = PRIORITY_FIELDS.findIndex(p => key.toLowerCase().includes(p.toLowerCase()));
    return {
      key,
      value,
      priority: priorityIndex === -1 ? 999 : priorityIndex
    };
  }).sort((a, b) => a.priority - b.priority);
  return sortedEntries.slice(0, maxFields);
}

/**
 * Render a status field with icon (no badge styling)
 */
function StatusValue(t0) {
  const $ = _c(8);
  const {
    isSuccess
  } = t0;
  const t1 = isSuccess ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex items-center gap-1 text-xs font-medium", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isSuccess) {
    t3 = isSuccess ? <IconCheckOutline18 className="h-3 w-3" /> : <IconXmarkOutline18 className="h-3 w-3" />;
    $[2] = isSuccess;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isSuccess ? "Success" : "Failed";
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = <span className={t2}>{t3}{t4}</span>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/**
 * Render text content with markdown support
 */
function TextContentValue(t0) {
  const $ = _c(13);
  const {
    value,
    maxLines: t1
  } = t0;
  const maxLines = t1 === undefined ? 4 : t1;
  let t2;
  let truncated;
  if ($[0] !== maxLines || $[1] !== value) {
    const lines = value.split("\n");
    truncated = lines.length > maxLines;
    t2 = truncated ? lines.slice(0, maxLines).join("\n") : value;
    $[0] = maxLines;
    $[1] = value;
    $[2] = t2;
    $[3] = truncated;
  } else {
    t2 = $[2];
    truncated = $[3];
  }
  const displayValue = t2;
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [remarkGfm];
    t4 = {
      p: _temp,
      strong: _temp2,
      em: _temp3,
      code: _temp4,
      a: _temp5,
      img: _temp6,
      ul: _temp7,
      ol: _temp8,
      li: _temp9
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== displayValue) {
    t5 = <ReactMarkdown remarkPlugins={t3} components={t4}>{displayValue}</ReactMarkdown>;
    $[6] = displayValue;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== truncated) {
    t6 = truncated && <span className="text-ds-text-disabled dark:text-ds-text-muted">...</span>;
    $[8] = truncated;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className="text-ds-text-secondary mt-1 text-xs">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}

/**
 * Render a transaction hash value, optionally as a clickable explorer link
 */
function _temp9(t0) {
  const {
    children: children_6
  } = t0;
  return <li className="leading-relaxed">{children_6}</li>;
}
function _temp8(t0) {
  const {
    children: children_5
  } = t0;
  return <ol className="ml-3 list-decimal">{children_5}</ol>;
}
function _temp7(t0) {
  const {
    children: children_4
  } = t0;
  return <ul className="ml-3 list-disc">{children_4}</ul>;
}
function _temp6() {
  return null;
}
function _temp5(t0) {
  const {
    children: children_3
  } = t0;
  return <span className="text-blue-600 underline dark:text-blue-400">{children_3}</span>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 text-3xs rounded px-1 py-0.5 font-mono">{children_2}</code>;
}
function _temp3(t0) {
  const {
    children: children_1
  } = t0;
  return <em className="italic">{children_1}</em>;
}
function _temp2(t0) {
  const {
    children: children_0
  } = t0;
  return <strong className="font-semibold">{children_0}</strong>;
}
function _temp(t0) {
  const {
    children
  } = t0;
  return <p className="mb-1 leading-relaxed last:mb-0">{children}</p>;
}
function TxHashValue(t0) {
  const $ = _c(12);
  const {
    value,
    chainId
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = formatTxHash(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formatted = t1;
  if (chainId) {
    let t2;
    if ($[2] !== chainId || $[3] !== value) {
      t2 = getExplorerTxUrl(chainId, value);
      $[2] = chainId;
      $[3] = value;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const explorerUrl = t2;
    let t3;
    if ($[5] !== explorerUrl || $[6] !== formatted || $[7] !== value) {
      t3 = <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-blue-600 hover:underline dark:text-blue-400" title={value}>{formatted}</a>;
      $[5] = explorerUrl;
      $[6] = formatted;
      $[7] = value;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t2;
  if ($[9] !== formatted || $[10] !== value) {
    t2 = <span className="text-ds-text-secondary min-w-0 truncate font-mono" title={value}>{formatted}</span>;
    $[9] = formatted;
    $[10] = value;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
export default function NodeOutputPreview({
  result,
  status,
  maxFields = 4,
  className,
  resultSchema,
  tokenDecimals,
  vaultShareDecimals
}: NodeOutputPreviewProps) {
  const fields = useMemo(() => {
    if (!result || typeof result !== "object") return [];
    return extractFields(result, maxFields);
  }, [result, maxFields]);

  // Extract chainId from result for explorer links (Solana actions include chainId in output)
  const resultChainId = useMemo(() => {
    if (!result) return null;
    const raw = result.chainId;
    if (typeof raw === "number") return raw;
    if (typeof raw === "string") {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }, [result]);
  const hasMoreFields = useMemo(() => {
    if (!result || typeof result !== "object") return false;
    return Object.keys(result).length > maxFields;
  }, [result, maxFields]);

  // Don't render if no result
  if (!result || fields.length === 0) {
    return null;
  }
  return <div className={cn("border-ds-border-default space-y-2 border-t pt-3", className)}>
      <div className="space-y-2">
        {fields.map(({
        key,
        value
      }) => {
        // Check if this is a status field
        const successStatus = isSuccessValue(key, value);
        if (successStatus !== null) {
          return <div key={key} className="flex items-center gap-2 text-xs">
                <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium">
                  {formatKey(key)}:
                </span>
                <StatusValue isSuccess={successStatus} />
              </div>;
        }

        // Check if this is a text content field that should render as markdown
        if (isTextContentField(key) && typeof value === "string" && value.length > 20) {
          return <div key={key} className="text-xs">
                <span className="text-ds-text-subtle dark:text-ds-text-disabled font-medium">{formatKey(key)}:</span>
                <TextContentValue value={value} />
              </div>;
        }

        // Transaction hash — render as clickable explorer link when both key and value match
        const isTxKey = TX_HASH_KEYS.some(k => key.toLowerCase().includes(k));
        if (typeof value === "string" && isTxKey && isTransactionHash(value)) {
          return <div key={key} className="flex items-start gap-2 text-xs">
                <span className="text-ds-text-subtle dark:text-ds-text-disabled flex-shrink-0 font-medium">
                  {formatKey(key)}:
                </span>
                <TxHashValue value={value} chainId={resultChainId} />
              </div>;
        }

        // Token amount fields — format with appropriate decimals
        if (getSchemaFieldAttr(key, "format", resultSchema) === "token-amount" && typeof value === "string" && /^[0-9]+$/.test(value)) {
          const decimals = getSchemaFieldAttr(key, "x-token-decimals", resultSchema) === "vault" ? vaultShareDecimals : tokenDecimals;
          if (decimals != null) {
            return <div key={key} className="flex items-start gap-2 text-xs">
                  <span className="text-ds-text-subtle dark:text-ds-text-disabled flex-shrink-0 font-medium">
                    {formatKey(key)}:
                  </span>
                  <span className="text-ds-text-secondary min-w-0 truncate font-mono" title={value}>
                    {formatTokenAmount(value, decimals)}
                  </span>
                </div>;
          }
        }

        // Default rendering
        return <div key={key} className="flex items-start gap-2 text-xs">
              <span className="text-ds-text-subtle dark:text-ds-text-disabled flex-shrink-0 font-medium">
                {formatKey(key)}:
              </span>
              <span className="text-ds-text-secondary min-w-0 truncate font-mono" title={typeof value === "string" ? value : JSON.stringify(value)}>
                {formatValue(value, 80, key)}
              </span>
            </div>;
      })}

        {hasMoreFields && <div className="micro-label">+{Object.keys(result).length - maxFields} more fields</div>}
      </div>
    </div>;
}